import { jsx } from 'react/jsx-runtime';
import { useMemo, createContext, use } from 'react';

const FeatureFlaggingContext = /*#__PURE__*/ createContext(null);
const FeatureFlaggingProvider = ({ children, strategy })=>{
    const StrategyProvider = strategy.Provider;
    const content = /*#__PURE__*/ jsx(FeatureFlaggingProviderContent, {
        strategy: strategy,
        children: children
    });
    return StrategyProvider ? /*#__PURE__*/ jsx(StrategyProvider, {
        children: content
    }) : content;
};
/*
 * Split out from `FeatureFlaggingProvider` so that `useData` relies on that provider's own context if defined.
 */ const FeatureFlaggingProviderContent = ({ children, strategy })=>{
    /*
   * Calls the strategy's `useData` exactly once, here. Passing the data down through context (instead of handing the strategy
   * to consumers) guarantees that, for Statsig, the client is not double-initialized and exposure logging is not doubled.
   */ const { getExperimentVariant, getFeatureFlag, isInitialized } = strategy.useData();
    const contextValue = useMemo(()=>({
            getExperimentVariant,
            getFeatureFlag,
            isInitialized
        }), [
        getExperimentVariant,
        getFeatureFlag,
        isInitialized
    ]);
    return /*#__PURE__*/ jsx(FeatureFlaggingContext, {
        value: contextValue,
        children: children
    });
};

/**
 * Variable to detect the test environment.
 * Testing runtimes (Jest and Vitest) automatically set the `NODE_ENV` environment variable to `test`.
 * Thanks to the `DefinePlugin` setup, the bundler minifier automatically strips the conditional code statement if NODE_ENV !== "test".
 * @see {@link https://vitest.dev/guide/migration.html#envs Node environment injection}.
 */ process.env.NODE_ENV === "test";
const LANGUAGE_TAGS_VALUES = [
    "de-DE",
    "de-AT",
    "en-GB"
];
new Set(LANGUAGE_TAGS_VALUES);
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[0]));
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[1]));
/**
 * Utility type that prevents a string from ending with a period,
 * enforcing that the message factory will always append its own terminating period.
 */ /**
 * Low-level factory that formats a two-line message with a labelled cause and a labelled solution.
 * Prefer the higher-level `createErrorMessage`, `createWarningMessage`, or `createNoteMessage`
 * instead of calling this directly.
 * @param configuration The configuration object.
 * @returns The formatted message string.
 */ const createMessage = ({ cause, solution })=>`${cause.header}: ${cause.message}.\n${solution.header}: ${solution.message}.\n`;
/**
 * A factory to format an error message in a consistent way.
 * @param configuration The configuration object.
 * @returns The formatted error message.
 */ const createErrorMessage = ({ cause, solution })=>createMessage({
        cause: {
            header: "❌ An error occurred",
            message: cause
        },
        solution: {
            header: "✅ To solve the issue",
            message: solution
        }
    });
/**
 * A React hook to enforce the Provider definition before using a context.
 * @param scope Metadata describing the context scope (including package and module name).
 * @param context The React `Context` to check.
 * @returns The context value.
 */ const useSafeContext = (scope, context)=>{
    const value = use(context);
    if (value === null) {
        // Apply the fail-fast principle to prevent any issues in production (e.g. the ones with default values
        // extraneous the current deployment target (such as applying `AVIV_THEME` while the current brand is SeLoger))
        throw new Error(createErrorMessage({
            cause: `The \`${scope.module}\` module is not able to retrieve contextual data`,
            solution: `Verify your Shell integration by checking if the \`${scope.package}\` package is effectively shared.\nIf the issue is only reproducible in your local environment, make sure to simulate the Shell environment with the right component(s)`
        }));
    }
    return value;
};

const useFeatureFlagging = ()=>{
    const { getExperimentVariant, getFeatureFlag, isInitialized } = useSafeContext({
        module: "useFeatureFlagging",
        package: "@iwt-core-web/feature-flagging"
    }, FeatureFlaggingContext);
    return {
        /**
     * Get an experiment value.
     * @param key The experiment key.
     * @param fallbackValue Returned when the experiment cannot be resolved (e.g., pending initialization, no assigned group, or an error).
     */ getExperimentVariant: (key, fallbackValue)=>getExperimentVariant(key) ?? fallbackValue,
        /**
     * Get a feature flag value.
     * @param key The feature flag/gate key.
     * @param fallbackValue Returned when the flag cannot be resolved (e.g., pending initialization, no assigned group, or an error).
     */ getFeatureFlag: (key, fallbackValue)=>getFeatureFlag(key) ?? fallbackValue,
        /**
     * Get the initialization status.
     * @returns `true` if the client is initialized, `false` otherwise.
     */ isInitialized,
        /**
     * @deprecated Will be removed in the next major version. Migrate to `getFeatureFlag(key, fallbackValue)` or `getExperimentVariant(key, fallbackValue)`.
     * Always returns `undefined`: flags are evaluated lazily, there is no bulk flags map anymore.
     */ getFeatureFlags: ()=>undefined,
        /**
     * @deprecated Will be removed in the next major version. Migrate to `getExperimentVariant(key, fallbackValue)`.
     * @returns The experiment variant, or `undefined` when it cannot be resolved.
     */ getVariation: (key)=>getExperimentVariant(key),
        /**
     * @deprecated Will be removed in the next major version. Migrate to `isInitialized`.
     */ isLoaded: isInitialized
    };
};

export { FeatureFlaggingProvider, useFeatureFlagging };
